import React, { useState, useEffect, useCallback } from "react";
import AdminService from "../services/AdminService";
import "./ActivityLogs.css";

// ─── Filter Constants ─────────────────────────────────────────────────
const USER_TYPE_OPTIONS = [
    { label: "All Users", value: "" },
    { label: "Admin", value: "ADMIN" },
    { label: "User", value: "USER" }
];

const ACTIVITY_TYPE_OPTIONS = [
    { label: "All Activities", value: "ALL", actions: [] },
    { label: "Login Activity", value: "LOGIN", actions: ["LOGIN", "LOGOUT", "FAILED_LOGIN"] },
    { label: "User Management", value: "USER_MGMT", actions: ["CREATE_USER", "UPDATE_USER", "DELETE_USER", "BLOCK_USER", "UNBLOCK_USER", "PERMANENT_BLOCK_USER"] },
    { label: "Product Actions", value: "PRODUCT", actions: ["APPROVE_AD", "REJECT_AD", "DELETE_PRODUCT", "UPDATE_PRODUCT", "BLOCK_PRODUCT", "PERMANENT_BLOCK_PRODUCT", "RESTORE_PRODUCT"] },
    { label: "Suspicious Activity", value: "SUSPICIOUS", actions: ["FAILED_LOGIN", "BLOCK_USER", "PERMANENT_BLOCK_USER", "BLOCK_PRODUCT", "PERMANENT_BLOCK_PRODUCT", "PRODUCT_AUTO_DISABLED"] }
];

const STATUS_OPTIONS = [
    { label: "All Status", value: "" },
    { label: "Success", value: "SUCCESS" },
    { label: "Failed", value: "FAILED" },
    { label: "Warning", value: "WARNING" }
];

// ─── Helper: format a datetime string ──────────────────────────────────
const formatDateTime = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return {
        date: d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
        time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })
    };
};

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const ActivityLogs = () => {
    // ── General State ──────────────────────────────────────────────────
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [stats, setStats] = useState({ totalLogs: 0, todayActivity: 0, criticalActions: 0 });
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    // ── Sessions State ─────────────────────────────────────────────────
    const [sessions, setSessions] = useState([]);
    const [sessionMeta, setSessionMeta] = useState({ totalSessions: 0, activeSessions: 0, completedSessions: 0 });

    // ── Filters ────────────────────────────────────────────────────────
    const [filterUserType, setFilterUserType] = useState("");
    const [filterActivity, setFilterActivity] = useState("ALL");
    const [filterStatus, setFilterStatus] = useState("");
    const [pagination, setPagination] = useState({ page: 0, size: 15, totalPages: 0, totalElements: 0 });

    // Is Login Activity mode? → use sessions endpoint
    const isSessionView = filterActivity === "LOGIN";

    // ── Fetch: General Logs ───────────────────────────────────────────
    const fetchLogs = useCallback(async () => {
        if (isSessionView) return; // skip if in session mode
        setLoading(true);
        try {
            const actObj = ACTIVITY_TYPE_OPTIONS.find(a => a.value === filterActivity);
            const request = {
                actions: actObj?.actions?.length ? actObj.actions : [],
                status: filterStatus,
                userType: filterUserType || null,
                page: pagination.page,
                size: pagination.size,
                sortBy: "createdAt",
                sortDir: "desc"
            };
            const res = await AdminService.getActivityLogs(request);
            const data = res.data?.data;
            if (data) {
                setRows(data.rows || []);
                setColumns(data.columns || []);
                setStats(data.stats || { totalLogs: 0, todayActivity: 0, criticalActions: 0 });
                setSummary(data.summary || null);
                setPagination(prev => ({
                    ...prev,
                    totalPages: data.pagination?.totalPages || 0,
                    totalElements: data.pagination?.totalElements || 0
                }));
            }
        } catch (err) {
            console.error("Error fetching activity logs:", err);
        } finally {
            setLoading(false);
        }
    }, [filterUserType, filterActivity, filterStatus, pagination.page, pagination.size, isSessionView]);

    // ── Fetch: Sessions ───────────────────────────────────────────────
    const fetchSessions = useCallback(async () => {
        if (!isSessionView) return;
        setLoading(true);
        try {
            const res = await AdminService.getUserSessions({
                status: filterStatus || null,
                userType: filterUserType || null,
                page: pagination.page,
                size: pagination.size
            });
            const data = res.data?.data;
            if (data) {
                setSessions(data.sessions || []);
                setSessionMeta({
                    totalSessions: data.totalSessions || 0,
                    activeSessions: data.activeSessions || 0,
                    completedSessions: data.completedSessions || 0
                });
                setPagination(prev => ({
                    ...prev,
                    totalPages: data.totalPages || 0,
                    totalElements: data.totalSessions || 0
                }));
            }
        } catch (err) {
            console.error("Error fetching sessions:", err);
        } finally {
            setLoading(false);
        }
    }, [isSessionView, pagination.page, pagination.size]);

    useEffect(() => {
        if (isSessionView) fetchSessions();
        else fetchLogs();
    }, [fetchLogs, fetchSessions, isSessionView]);

    // ── Export CSV ─────────────────────────────────────────────────────
    const handleExport = async () => {
        try {
            const res = await AdminService.exportActivityLogs({});
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `activity_logs_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    // ── Page helpers ──────────────────────────────────────────────────
    const goPage = (p) => setPagination(prev => ({ ...prev, page: p }));
    const resetPage = () => setPagination(prev => ({ ...prev, page: 0 }));

    const handleRefresh = () => {
        if (pagination.page !== 0) resetPage();
        else if (isSessionView) fetchSessions();
        else fetchLogs();
    };

    // ── Pagination range ─────────────────────────────────────────────
    const buildPageNumbers = () => {
        const { page, totalPages } = pagination;
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
        const pages = [];
        pages.push(0);
        if (page > 3) pages.push("...");
        for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) pages.push(i);
        if (page < totalPages - 4) pages.push("...");
        pages.push(totalPages - 1);
        return pages;
    };

    // ── Render helpers ───────────────────────────────────────────────
    const renderStatusBadge = (val) => {
        const s = (val || "INFO").toUpperCase();
        return <span className={`al-badge al-badge--${s.toLowerCase()}`}>{s}</span>;
    };

    const renderActionTag = (val) => {
        if (!val) return "—";
        return <span className="al-action-tag">{val.replace(/_/g, " ")}</span>;
    };

    const renderUser = (val) => (
        <div className="al-user-cell">
            <div className="al-avatar">{val?.initial || "U"}</div>
            <div className="al-user-meta">
                <span className="al-user-name">{val?.name || "System"}</span>
                <span className="al-user-id">ID: {val?.id || "—"}</span>
            </div>
        </div>
    );

    const renderDateTime = (val) => {
        const f = formatDateTime(val);
        if (!f) return <span className="al-muted">--</span>;
        return (
            <div className="al-datetime">
                <span className="al-date">{f.date}</span>
                <span className="al-time">{f.time}</span>
            </div>
        );
    };

    const renderCell = (row, col) => {
        const val = row[col.key];
        if (col.type === "user") return renderUser(val);
        if (col.key === "status") return renderStatusBadge(val);
        if (col.type === "badge") return renderActionTag(val);
        if (col.type === "datetime") return renderDateTime(val);
        if (col.type === "duration") return <span className="al-duration">{val || "—"}</span>;
        if (col.type === "number") return <span className="al-number">{val ?? "—"}</span>;
        return <span className="al-text">{val || "—"}</span>;
    };

    // ─── Skeleton ────────────────────────────────────────────────────
    const SkeletonTable = () => (
        <div className="al-skeleton">
            <div className="al-skeleton-header" />
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="al-skeleton-row">
                    {Array.from({ length: 6 }).map((__, j) => (
                        <div key={j} className="al-skeleton-cell" />
                    ))}
                </div>
            ))}
        </div>
    );

    // ─── Empty State ─────────────────────────────────────────────────
    const EmptyState = () => (
        <div className="al-empty">
            <div className="al-empty-icon"><i className="fa fa-inbox" /></div>
            <h3>{isSessionView ? "No Active Sessions Found" : "No Data Found"}</h3>
            <p>{isSessionView ? "There are no login/logout sessions recorded yet." : "No activity records match the current filter criteria."}</p>
            <button className="al-btn al-btn--outline" onClick={() => { setFilterUserType(""); setFilterActivity("ALL"); setFilterStatus(""); resetPage(); }}>
                <i className="fa fa-rotate-left" /> Clear All Filters
            </button>
        </div>
    );

    // ─── Sessions Table ──────────────────────────────────────────────
    const SessionsTable = () => (
        <div className="al-table-wrap">
            <table className="al-table">
                <thead>
                    <tr>
                        <th>User</th>
                        <th>Login Time</th>
                        <th>Logout Time</th>
                        <th>Time Spent</th>
                        <th>IP Address</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {sessions.map((s, idx) => (
                        <tr key={s.id || idx}>
                            <td>
                                <div className="al-user-cell">
                                    <div className="al-avatar">{s.userInitial || "U"}</div>
                                    <div className="al-user-meta">
                                        <span className="al-user-name">{s.userName || "Unknown"}</span>
                                        <span className="al-user-id">ID: {s.userId}</span>
                                    </div>
                                </div>
                            </td>
                            <td>{renderDateTime(s.loginTime)}</td>
                            <td>{s.logoutTime ? renderDateTime(s.logoutTime) : <span className="al-muted">--</span>}</td>
                            <td>
                                {s.timeSpent === "Active" ? (
                                    <span className="al-active-badge">
                                        <span className="al-active-dot" /> Active
                                    </span>
                                ) : (
                                    <span className="al-duration">{s.timeSpent}</span>
                                )}
                            </td>
                            <td><span className="al-text">{s.ipAddress || "—"}</span></td>
                            <td>
                                {s.status === "Active" ? (
                                    <span className="al-badge al-badge--active">ACTIVE</span>
                                ) : (
                                    <span className="al-badge al-badge--success">COMPLETED</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // ─── General Table ───────────────────────────────────────────────
    const GeneralTable = () => (
        <div className="al-table-wrap">
            <table className="al-table">
                <thead>
                    <tr>
                        {columns.map(col => <th key={col.key}>{col.label}</th>)}
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr key={row.id || idx}>
                            {columns.map(col => <td key={col.key}>{renderCell(row, col)}</td>)}
                            <td>{renderStatusBadge(row.status)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const hasData = isSessionView ? sessions.length > 0 : rows.length > 0;

    // ═════════════════════════════════════════════════════════════════
    // JSX
    // ═════════════════════════════════════════════════════════════════
    return (
        <div className="al-container">
            {/* ────── KPI Stats ────── */}
            <div className="al-stats-grid">
                {isSessionView ? (
                    <>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--blue"><i className="fa fa-right-to-bracket" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : sessionMeta.totalSessions}</h4>
                                <p>Total Sessions</p>
                            </div>
                        </div>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--green"><i className="fa fa-circle-check" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : sessionMeta.completedSessions}</h4>
                                <p>Completed</p>
                            </div>
                        </div>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--orange"><i className="fa fa-signal" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : sessionMeta.activeSessions}</h4>
                                <p>Active Now</p>
                            </div>
                        </div>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--red"><i className="fa fa-clock" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : "LIVE"}</h4>
                                <p>Tracking</p>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--blue"><i className="fa fa-list-ul" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : Number(stats.totalLogs).toLocaleString()}</h4>
                                <p>Total Records</p>
                            </div>
                        </div>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--orange"><i className="fa fa-bolt" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : Number(stats.todayActivity).toLocaleString()}</h4>
                                <p>Today's Activity</p>
                            </div>
                        </div>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--green"><i className="fa fa-shield" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : (stats.totalLogs > 0 ? "SECURE" : "N/A")}</h4>
                                <p>System Health</p>
                            </div>
                        </div>
                        <div className="al-stat-card">
                            <div className="al-stat-icon al-stat-icon--red"><i className="fa fa-exclamation-triangle" /></div>
                            <div className="al-stat-body">
                                <h4>{loading ? "—" : Number(stats.criticalActions).toLocaleString()}</h4>
                                <p>Critical Alerts</p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ────── Filter Bar ────── */}
            <div className="al-card al-filter-card">
                <div className="al-filter-row">
                    <div className="al-filter-group">
                        <label className="al-filter-label"><i className="fa fa-user" /> User Type</label>
                        <select className="al-select" value={filterUserType} onChange={e => { setFilterUserType(e.target.value); resetPage(); }}>
                            {USER_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="al-filter-group">
                        <label className="al-filter-label"><i className="fa fa-layer-group" /> Activity Type</label>
                        <select className="al-select" value={filterActivity} onChange={e => { setFilterActivity(e.target.value); resetPage(); }}>
                            {ACTIVITY_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="al-filter-group">
                        <label className="al-filter-label"><i className="fa fa-filter" /> Status</label>
                        <select className="al-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); resetPage(); }}>
                            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="al-filter-actions">
                        <button className="al-btn al-btn--primary" onClick={handleRefresh} title="Refresh data">
                            <i className={`fa fa-refresh ${loading ? "fa-spin" : ""}`} /> Refresh
                        </button>
                        <button className="al-btn al-btn--outline" onClick={handleExport} title="Export CSV">
                            <i className="fa fa-download" /> Export
                        </button>
                    </div>
                </div>
            </div>

            {/* ────── Session Mode Banner ────── */}
            {isSessionView && !loading && sessions.length > 0 && (
                <div className="al-summary-strip">
                    <span><i className="fa fa-right-to-bracket" /> <b>{sessionMeta.totalSessions}</b> total sessions</span>
                    <span><i className="fa fa-circle" style={{ color: "#10b981", fontSize: 8 }} /> <b>{sessionMeta.activeSessions}</b> currently active</span>
                    <span><i className="fa fa-circle-check" /> <b>{sessionMeta.completedSessions}</b> completed</span>
                </div>
            )}

            {/* ────── Non-session summary ────── */}
            {!isSessionView && summary && (
                <div className="al-summary-strip">
                    {summary.totalSessions != null && <span><i className="fa fa-right-to-bracket" /> <b>{summary.totalSessions}</b> sessions</span>}
                    {summary.activeSessions != null && <span><i className="fa fa-circle" style={{ color: "#10b981", fontSize: 8 }} /> <b>{summary.activeSessions}</b> active now</span>}
                    {summary.avgDuration && <span><i className="fa fa-clock" /> Avg. duration: <b>{summary.avgDuration}</b></span>}
                    {summary.totalFailedAttempts != null && <span><i className="fa fa-triangle-exclamation" /> <b>{summary.totalFailedAttempts}</b> failed attempts</span>}
                    {summary.affectedUsers != null && <span><i className="fa fa-users" /> <b>{summary.affectedUsers}</b> affected users</span>}
                </div>
            )}

            {/* ────── Table Card ────── */}
            <div className="al-card al-table-card">
                {loading && (
                    <div className="al-table-loading-overlay">
                        <div className="al-spinner-pulse" />
                        <span>Refreshing Data...</span>
                    </div>
                )}
                {loading && rows.length === 0 && sessions.length === 0 ? (
                    <SkeletonTable />
                ) : !hasData && !loading ? (
                    <EmptyState />
                ) : isSessionView ? (
                    <SessionsTable />
                ) : (
                    <GeneralTable />
                )}
            </div>

            {/* ────── Pagination ────── */}
            {!loading && pagination.totalPages > 1 && (
                <div className="al-pagination">
                    <div className="al-page-info">
                        Showing <b>{pagination.page * pagination.size + 1}</b>–<b>{Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)}</b> of <b>{pagination.totalElements}</b> records
                    </div>
                    <div className="al-page-controls">
                        <button className="al-page-btn" disabled={pagination.page === 0} onClick={() => goPage(pagination.page - 1)}>
                            <i className="fa fa-chevron-left" />
                        </button>
                        {buildPageNumbers().map((p, i) =>
                            p === "..." ? (
                                <span key={`e${i}`} className="al-page-ellipsis">…</span>
                            ) : (
                                <button key={p} className={`al-page-btn ${pagination.page === p ? "active" : ""}`} onClick={() => goPage(p)}>
                                    {p + 1}
                                </button>
                            )
                        )}
                        <button className="al-page-btn" disabled={pagination.page >= pagination.totalPages - 1} onClick={() => goPage(pagination.page + 1)}>
                            <i className="fa fa-chevron-right" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivityLogs;
